import { defineStore } from "pinia";
import { ref } from "vue";
import { collection, query, doc, updateDoc, addDoc, where, onSnapshot, serverTimestamp, getDoc, deleteDoc } from "firebase/firestore";
import { db } from "../js/firebase";
import { logger } from '../services/loggerService';
import { useFormBuilderStore } from './formBuilderStore';
import { useUserAuthStore } from './userAuthStore';
import { deliverableService } from '../services/deliverableService';
import { projectService } from '../services/projectService';
import { PROJECT_STATUS, resolveSubmitStatus } from '../utils/projectUtils';
import { contractService } from '../services/contractService';
import { applyMsaOverrideToBriefSections } from '../services/pricingCalculatorService';

const projectCollectionRef = collection(db, "projects");

export const useClientProjectsStore = defineStore('clientProjects', () => {
  // ─── External Stores ───────────────────────────────────────
  const userAuthStore = useUserAuthStore();
  const formBuilderStore = useFormBuilderStore();

  // state
  const items = ref({});
  const loading = ref(false);

  // Deliverables state
  const deliverables = ref([]);
  const deliverablesLoading = ref(false);
  let deliverablesUnsubscribe = null;
  let projectsUnsubscribe = null;

  // actions
  function getCompanyProjects(companyId) {
    loading.value = true;
    
    logger.info("[ClientProjectsStore] getCompanyProjects called", { companyId });
    
    const q = query(
      projectCollectionRef,
      where("company", "==", companyId),
      where("status", "!=", PROJECT_STATUS.ARCHIVED)
    );

    // Clean up previous subscription if exists
    if (projectsUnsubscribe) {
      projectsUnsubscribe();
      projectsUnsubscribe = null;
    }

    projectsUnsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const projectData = change.doc.data();
        const project = {
          id: change.doc.id,
          ...projectData,
          dateUpdated: new Date(projectData.dateUpdated).toLocaleString(),
        };
        
        // Debug log for projects with "Dates Needed" status
        if (project.status === PROJECT_STATUS.DATES_NEEDED) {
          logger.info("[ClientProjectsStore] Loading project with Dates Needed status:", {
            id: project.id,
            name: project.name,
            pointsTotal: project.pointsTotal,
            status: project.status,
            type: project.type,
            requireConsecutive: project.requireConsecutive
          });
        }

        if (change.type === "added" || change.type === "modified") {
          // Only add/modify if the project is not archived
          if (project.status !== PROJECT_STATUS.ARCHIVED) {
            items.value[project.id] = project;
          } else {
            // If project is archived, remove it from local state
            delete items.value[project.id];
          }
        }
        if (change.type === "removed") {
          delete items.value[project.id];
        }
      });
      
      loading.value = false;
    });
  }

  // Update a project (for date selection)
  async function updateProject(projectId, projectData) {
    if (!projectId || !projectData) {
      logger.error('[ClientProjectsStore] Invalid project update attempt - missing data');
      throw new Error('Invalid project data or ID provided');
    }

    try {
      loading.value = true;
      
      // Function to recursively remove undefined values
      const removeUndefined = (obj) => {
        if (obj === null) {
          return null;
        }
        
        if (obj === undefined) {
          return undefined;
        }
        
        if (Array.isArray(obj)) {
          const cleanedArray = obj
            .map(item => removeUndefined(item))
            .filter(item => item !== undefined);
          return cleanedArray.length > 0 ? cleanedArray : [];
        }
        
        if (typeof obj === 'object' && obj.constructor === Object) {
          const cleaned = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
              const cleanedValue = removeUndefined(value);
              if (cleanedValue !== undefined) {
                cleaned[key] = cleanedValue;
              }
            }
          }
          return Object.keys(cleaned).length > 0 ? cleaned : {};
        }
        
        if (typeof obj === 'object') {
          return obj;
        }
        
        return obj;
      };
      
      // Prepare update data and clean it
      const updateData = removeUndefined({
        ...projectData,
        dateUpdated: serverTimestamp()
      });
      
      // Update Firestore document directly
      const docRef = doc(projectCollectionRef, projectId);
      await updateDoc(docRef, updateData);

      // Update local state
      items.value[projectId] = {
        ...items.value[projectId],
        ...updateData,
        dateUpdated: new Date().toLocaleString()
      };
      
      logger.info('[ClientProjectsStore] Project updated successfully', { projectId });
      return { success: true };
    } catch (error) {
      logger.error('[ClientProjectsStore] Failed to update project:', {
        projectId,
        error: error.message
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  // Delete a project (only if status is "Draft")
  async function deleteProject(projectId) {
    if (!projectId) {
      logger.error('[ClientProjectsStore] Invalid project delete attempt - missing ID');
      throw new Error('Project ID is required');
    }

    const project = items.value[projectId];
    if (!project) {
      logger.error('[ClientProjectsStore] Project not found for deletion', { projectId });
      throw new Error('Project not found');
    }

    if (project.status !== PROJECT_STATUS.DRAFT) {
      logger.error('[ClientProjectsStore] Cannot delete non-draft project', { 
        projectId, 
        status: project.status 
      });
      throw new Error('Only draft projects can be deleted');
    }

    try {
      loading.value = true;

      // Actually delete the document from Firestore
      const docRef = doc(projectCollectionRef, projectId);
      await deleteDoc(docRef);

      // Immediately remove from local state
      delete items.value[projectId];

      logger.info('[ClientProjectsStore] Project deleted from Firestore', { projectId });
      return { success: true };
    } catch (error) {
      logger.error('[ClientProjectsStore] Failed to delete project:', {
        projectId,
        error: error.message
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create a new project in Firestore and add to local state
   * @param {Object} projectData - New project data
   * @param {Object} options - Creation options { isClone: boolean, sourceProjectId: string, submitForReview: boolean }
   */
  async function createProject(projectData, options = {}) {
    if (!projectData || !projectData.name || !projectData.companyId) {
      logger.error('[ClientProjectsStore] Invalid project creation attempt - missing required data');
      throw new Error('Project name and company ID are required');
    }

    loading.value = true;

    try {
      logger.info('[ClientProjectsStore] Creating new project', {
        name: projectData.name,
        companyId: projectData.companyId,
        type: projectData.type,
        isClone: options.isClone || false,
        sourceProjectId: options.sourceProjectId
      });

      // Fetch the company's default contract ID (and its agency link — resolved
      // here rather than at each of the six call sites, so a caller that forgets
      // to pass it can't silently skip the agency review gate)
      let defaultContractId = null;
      let companyAgencyId = null;
      try {
        const companyRef = doc(db, "companies", projectData.companyId);
        const companyDoc = await getDoc(companyRef);
        
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          defaultContractId = companyData.defaultContractId || null;
          companyAgencyId = companyData.agencyId || null;
          
          if (defaultContractId) {
            logger.info('[ClientProjectsStore] Found default contract for company', {
              companyId: projectData.companyId,
              defaultContractId
            });
          } else {
            logger.info('[ClientProjectsStore] No default contract set for company', {
              companyId: projectData.companyId
            });
          }
        }
      } catch (error) {
        logger.warn('[ClientProjectsStore] Failed to fetch company default contract', {
          error: error.message,
          companyId: projectData.companyId
        });
      }

      // Get current user for tracking

      // Prepare the base project data
      // Spread projectData first so explicit values below override any values from the caller
      const newProjectData = {
        ...projectData,
        name: projectData.name.trim(),
        company: projectData.companyId,
        companyName: projectData.companyName,
        // Agency-linked companies get the agency's review gate first; that gate
        // then hands off to In Review, so everything downstream is unchanged.
        status: options.activateProject
          ? resolveSubmitStatus({ agencyId: companyAgencyId, type: projectData.type })
          : PROJECT_STATUS.DRAFT,
        // Stamped so the agency review queue is a single indexed query rather
        // than a chunked where('company','in',[...]) with its 30-value cap
        agencyId: companyAgencyId,
        contractId: projectData.contractId || defaultContractId, // Show contract takes priority over company default
        createdBy: userAuthStore.user?.id || null,
        createdByName: userAuthStore.user?.name || userAuthStore.user?.email || 'Unknown',
        dateCreated: serverTimestamp(),
        dateUpdated: serverTimestamp(),
        dateSubmitted: options.activateProject ? serverTimestamp() : null, // Only set if activating
      };

      // Remove any non-serializable or unwanted properties
      delete newProjectData.companyId; // Use 'company' field instead
      // Default type to 'project' if not explicitly set (episodes will pass 'episode')
      if (!newProjectData.type) {
        newProjectData.type = 'project'
      }
      delete newProjectData.id;

      // Handle cloning logic
      if (options.isClone && options.sourceProjectId) {
        logger.info('[ClientProjectsStore] Cloning project data', {
          sourceProjectId: options.sourceProjectId,
          targetName: projectData.name
        });

        // Get source project data
        const sourceProject = items.value[options.sourceProjectId];
        if (!sourceProject) {
          throw new Error(`Source project ${options.sourceProjectId} not found for cloning`);
        }

        // Note: Brief data is typically stored under misspelled "breif" field in existing projects
        logger.debug('[ClientProjectsStore] Checking for brief data to clone:', {
          sourceProjectId: options.sourceProjectId,
          sourceProjectName: sourceProject.name,
          hasBriefData: !!sourceProject.briefData,
          hasBreif: !!sourceProject.breif, // misspelled field where data is usually stored
          hasBrief: !!sourceProject.brief
        });

        // Try different possible field names for brief data
        let briefDataToCopy = null;
        let fieldName = null;

        if (sourceProject.breif) {
          briefDataToCopy = sourceProject.breif;
          fieldName = 'breif';
        } else if (sourceProject.briefData) {
          briefDataToCopy = sourceProject.briefData;
          fieldName = 'briefData';
        } else if (sourceProject.brief) {
          briefDataToCopy = sourceProject.brief;
          fieldName = 'brief';
        } else if (sourceProject.formData) {
          briefDataToCopy = sourceProject.formData;
          fieldName = 'formData';
        } else if (sourceProject.data) {
          briefDataToCopy = sourceProject.data;
          fieldName = 'data';
        }

        if (briefDataToCopy) {
          // Store under the "breif" field (misspelled) for consistency with existing system
          newProjectData.breif = briefDataToCopy;
          
          logger.info('[ClientProjectsStore] Brief data cloned successfully', {
            sourceProjectId: options.sourceProjectId,
            sourceProjectName: sourceProject.name,
            fieldName: fieldName,
            briefDataSize: JSON.stringify(briefDataToCopy).length
          });
        } else {
          logger.warn('[ClientProjectsStore] No brief data found to clone', {
            sourceProjectId: options.sourceProjectId,
            sourceProjectName: sourceProject.name
          });
        }

        // Set the type from source project
        newProjectData.type = sourceProject.type;
      } else if (!options.isClone && projectData.type) {
        // Handle creating new blank project with initial brief data from intake configuration
        logger.info('[ClientProjectsStore] Creating new blank project with initial brief data', {
          projectType: projectData.type,
          targetName: projectData.name,
          hasPassedBrief: !!projectData.breif
        });

        // If brief data is already provided (from wizard), use it
        if (projectData.breif && projectData.breif.sections) {
          logger.info('[ClientProjectsStore] Using provided brief data from wizard', {
            projectType: projectData.type,
            sectionCount: projectData.breif.sections.length
          });
          // Keep the provided brief data as is
        } else {
          // Generate initial brief data from the selected intake configuration
          try {
            // Ensure intake configurations are loaded
            if (!formBuilderStore.initialized) {
              await formBuilderStore.loadConfigurations();
            }

            // Generate initial brief data from the selected intake configuration
            let initialBriefData = formBuilderStore.generateFormData(projectData.type);

            if (initialBriefData && Array.isArray(initialBriefData) && initialBriefData.length > 0) {
              // Apply company MSA custom pricing to calculator sections (rate snapshot)
              const hasCalculator = initialBriefData.some(s => s.type === 'pricing-calculator');
              if (hasCalculator && projectData.companyId) {
                try {
                  const msaPricing = await contractService.getMsaPricingForCompany(projectData.companyId);
                  if (msaPricing) {
                    const result = applyMsaOverrideToBriefSections(initialBriefData, msaPricing);
                    initialBriefData = result.sections;
                    if (result.warnings.length) {
                      logger.warn('[ClientProjectsStore] MSA pricing override drift', { warnings: result.warnings });
                    }
                  }
                } catch (pricingError) {
                  logger.error('[ClientProjectsStore] Failed to apply MSA custom pricing, using standard rates', {
                    error: pricingError.message,
                    companyId: projectData.companyId
                  });
                }
              }

              // Store under the "breif" field (misspelled) for consistency with existing system
              newProjectData.breif = { sections: initialBriefData };

              logger.info('[ClientProjectsStore] Initial brief data created from intake configuration', {
                projectType: projectData.type,
                sectionCount: initialBriefData.length,
                briefDataSize: JSON.stringify(initialBriefData).length
              });
            } else {
              logger.error('[ClientProjectsStore] No intake configuration found for project type', {
                projectType: projectData.type
              });
              throw new Error(`No intake configuration found for project type: ${projectData.type}`);
            }
          } catch (error) {
            logger.error('[ClientProjectsStore] Failed to generate initial brief data:', {
              projectType: projectData.type,
              error: error.message
            });
            throw error; // Re-throw the error to prevent project creation
          }
        }
      }

      // Function to recursively remove undefined values
      const removeUndefined = (obj) => {
        if (obj === null) {
          return null;
        }
        
        if (obj === undefined) {
          return undefined;
        }
        
        if (Array.isArray(obj)) {
          const cleanedArray = obj
            .map(item => removeUndefined(item))
            .filter(item => item !== undefined);
          return cleanedArray.length > 0 ? cleanedArray : [];
        }
        
        if (typeof obj === 'object' && obj.constructor === Object) {
          const cleaned = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
              const cleanedValue = removeUndefined(value);
              if (cleanedValue !== undefined) {
                cleaned[key] = cleanedValue;
              }
            }
          }
          return Object.keys(cleaned).length > 0 ? cleaned : {};
        }
        
        if (typeof obj === 'object') {
          return obj;
        }
        
        return obj;
      };

      // Clean the data for Firebase
      const firebaseCleanedData = removeUndefined(newProjectData);

      logger.debug('[ClientProjectsStore] Cleaned project data for creation:', {
        originalKeys: Object.keys(newProjectData).length,
        cleanedKeys: Object.keys(firebaseCleanedData).length,
        hasCompany: !!firebaseCleanedData.company,
        hasName: !!firebaseCleanedData.name,
        hasBriefData: !!firebaseCleanedData.breif
      });

      // Create the project in Firestore
      const docRef = await addDoc(projectCollectionRef, firebaseCleanedData);

      // Prepare the local state data with the new ID
      // Note: serverTimestamp() returns a sentinel value, so we use current date for local state
      const now = new Date();
      const localProjectData = {
        id: docRef.id,
        ...firebaseCleanedData,
        dateUpdated: now.toLocaleString(),
        dateCreated: now.toLocaleString(),
        dateSubmitted: now.toLocaleString()
      };

      // Add to local state
      items.value[docRef.id] = localProjectData;

      logger.info('[ClientProjectsStore] Project created successfully', {
        projectId: docRef.id,
        name: projectData.name,
        companyId: projectData.companyId,
        isClone: options.isClone || false
      });

      // Log project creation success
      if (options.activateProject) {
        logger.info('[ClientProjectsStore] Project created and submitted for review', {
          projectId: docRef.id,
          // Agency-linked companies land in Agency Review, not In Review
          status: newProjectData.status
        });
      } else {
        logger.info('[ClientProjectsStore] Project saved as draft', {
          projectId: docRef.id,
          status: PROJECT_STATUS.DRAFT
        });
      }

      // Log project creation activity to Firestore
      try {
        await projectService.logActivity(
          docRef.id,
          'created',
          'status',
          null,
          options.activateProject ? PROJECT_STATUS.IN_REVIEW : PROJECT_STATUS.DRAFT,
          options.activateProject ? 'Project submitted for review' : 'Project saved as draft'
        );
      } catch (logError) {
        // Don't fail project creation if logging fails
        logger.warn('[ClientProjectsStore] Failed to log project creation activity', {
          projectId: docRef.id,
          error: logError.message
        });
      }

      return {
        success: true,
        projectId: docRef.id,
        project: localProjectData
      };

    } catch (error) {
      logger.error('[ClientProjectsStore] Failed to create project:', {
        name: projectData.name,
        companyId: projectData.companyId,
        isClone: options.isClone || false,
        error: error.message
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  // Reset store state
  /**
   * Fetch deliverables for a project (read-only for clients)
   * @param {String} projectId - Project ID to fetch deliverables for
   * @param {String} companyId - Optional company ID for permission filtering
   */
  async function fetchProjectDeliverables(projectId, companyId = null) {
    if (!projectId) {
      logger.error('[ClientProjects] No project ID provided for fetching deliverables');
      return;
    }

    try {
      deliverablesLoading.value = true;

      // Get companyId from project if not provided
      if (!companyId && items.value[projectId]) {
        companyId = items.value[projectId].company;
      }

      // Unsubscribe from previous listener if exists
      if (deliverablesUnsubscribe) {
        deliverablesUnsubscribe();
      }

      // For client users, use one-time fetch instead of real-time subscription
      // Real-time subscriptions have stricter permission requirements
      const projectDeliverables = await deliverableService.getProjectDeliverables(projectId);
      deliverables.value = projectDeliverables;

      logger.info('[ClientProjects] Fetched deliverables for project', {
        projectId,
        count: projectDeliverables.length
      });
    } catch (error) {
      logger.error('[ClientProjects] Failed to fetch deliverables:', {
        projectId,
        error: error.message
      });
      deliverables.value = [];
    } finally {
      deliverablesLoading.value = false;
    }
  }

  function reset() {
    items.value = {};
    loading.value = false;
    deliverables.value = [];
    deliverablesLoading.value = false;

    // Cleanup projects subscription
    if (projectsUnsubscribe) {
      projectsUnsubscribe();
      projectsUnsubscribe = null;
    }

    // Cleanup deliverables subscription
    if (deliverablesUnsubscribe) {
      deliverablesUnsubscribe();
      deliverablesUnsubscribe = null;
    }
  }

  return {
    items,
    loading,
    getCompanyProjects,
    updateProject,
    deleteProject,
    createProject,
    reset,
    // Deliverables
    deliverables,
    deliverablesLoading,
    fetchProjectDeliverables,
  };
}); 